import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Loader2, Radio } from "lucide-react";
import { toast } from "sonner";

const LiveToggle = () => {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data, error } = await supabase
          .from("site_settings")
          .select("value")
          .eq("key", "live_enabled")
          .maybeSingle();
        if (error) throw error;
        if (data?.value) {
          const value = data.value as { enabled: boolean };
          setEnabled(value.enabled ?? false);
        }
      } catch (error) {
        console.error("Error fetching live status:", error);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const toggle = async () => {
    setUpdating(true);
    const newValue = !enabled;
    try {
      const { error } = await supabase
        .from("site_settings")
        .update({
          value: { enabled: newValue },
          updated_at: new Date().toISOString(),
        })
        .eq("key", "live_enabled");
      if (error) throw error;
      setEnabled(newValue);
      toast.success(newValue ? "Live activé — le stream est visible" : "Live désactivé — les offres s'affichent");
    } catch (error) {
      console.error("Error updating live status:", error);
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/50">
        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Chargement...</span>
      </div>
    );
  }

  return (
    <div
      className={`flex items-center justify-between px-3 py-2.5 rounded-lg border transition-all ${
        enabled
          ? "bg-red-500/10 border-red-500/30"
          : "bg-muted/50 border-border"
      }`}
    >
      <div className="flex items-center gap-2">
        <Radio className={`w-4 h-4 ${enabled ? "text-red-400" : "text-muted-foreground"}`} />
        <div className="flex flex-col">
          <span className="text-sm font-medium text-foreground">Live Stream</span>
          <span className="text-xs text-muted-foreground">
            {enabled ? "En direct — stream visible" : "Désactivé — offres affichées"}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {enabled && (
          <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-red-500/50 text-red-400 animate-pulse">
            LIVE
          </Badge>
        )}
        <Switch
          checked={enabled}
          onCheckedChange={toggle}
          disabled={updating}
          className={enabled ? "data-[state=checked]:bg-red-500" : ""}
        />
      </div>
    </div>
  );
};

export default LiveToggle;
